import React, { useEffect } from "react";
import { Box, Container, Typography, Button } from "@mui/material";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { useTranslation } from "react-i18next";
import VideoBackground from "./VideoBackground";

const Hero = ({ onBookingClick }) => {
    const { t } = useTranslation();

    /* Retire the boot backdrop from public/index.html once this hero has
       painted over it. It exists only to fill the window with the hero image
       straight from the HTML while the bundle downloads (First Contentful Paint
       0.8 s instead of a black screen).

       It CANNOT serve as the hero's actual image: it sits at z-index 0 behind
       #root, and this component's own opaque background paints over it — an
       earlier attempt to drop VideoBackground's poster and rely on the backdrop
       left the hero solid black. The poster stays; the backdrop is scaffolding.

       Two frames of grace so the hero is definitely on screen before the layer
       underneath goes away. */
    useEffect(() => {
        const id = requestAnimationFrame(() =>
            requestAnimationFrame(() => document.getElementById("hero-boot")?.remove()),
        );
        return () => cancelAnimationFrame(id);
    }, []);

    return (
        <Box
            id="hero"
            sx={{
                minHeight: "100vh",
                height: "100vh",
                "@supports (height: 100dvh)": {
                    minHeight: "100dvh",
                    height: "100dvh",
                },
                display: "flex",
                flexDirection: "column",
                position: "relative",
                overflow: "hidden",
                color: "#ffffff",
                background: "#000000",
            }}
        >
            <VideoBackground
                src="/videos/hair-hero.mp4"
                poster="/videos/hair-hero-poster.jpg"
                priority
                overlay="linear-gradient(180deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.7) 60%, rgba(0,0,0,0.85) 100%)"
                sx={{ zIndex: 0 }}
            />
            <style>{`
                @keyframes hero-rise {
                    from { opacity: 0; transform: translateY(28px); }
                    to   { opacity: 1; transform: translateY(0); }
                }
                @keyframes hero-fade {
                    from { opacity: 0; }
                    to   { opacity: 1; }
                }
                @keyframes hero-line-grow {
                    from { transform: scaleX(0); }
                    to   { transform: scaleX(1); }
                }
                @keyframes hero-bounce {
                    0%, 100% { transform: translateY(0); opacity: 0.45; }
                    50%      { transform: translateY(8px); opacity: 0.85; }
                }
                @keyframes hero-grain {
                    0%   { transform: translate(0, 0); }
                    20%  { transform: translate(-1%, 1%); }
                    40%  { transform: translate(-2%, -1%); }
                    60%  { transform: translate(1%, -2%); }
                    80%  { transform: translate(2%, 1%); }
                    100% { transform: translate(0, 0); }
                }
            `}</style>

            {/* ── Subtle grain overlay ── */}
            <Box
                sx={{
                    position: "absolute",
                    inset: "-10%",
                    opacity: 0.08,
                    pointerEvents: "none",
                    zIndex: 1,
                    backgroundImage:
                        "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 1 0'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
                    animation: "hero-grain 8s steps(6) infinite",
                }}
            />

            {/* ── Soft champagne wash ── */}
            <Box
                sx={{
                    position: "absolute",
                    inset: 0,
                    pointerEvents: "none",
                    zIndex: 1,
                    background:
                        "radial-gradient(ellipse at 80% 20%, rgba(255, 255, 255, 0.06) 0%, transparent 55%), radial-gradient(ellipse at 10% 90%, rgba(255, 255, 255, 0.04) 0%, transparent 55%)",
                }}
            />

            {/* ═══════════════════════════════════════════
                Top brand mark — minimalist line
            ═══════════════════════════════════════════ */}
            <Container
                maxWidth="xl"
                sx={{
                    position: "relative",
                    zIndex: 2,
                    pt: { xs: "5.5rem", md: "7rem" },
                    px: { xs: 3, md: 6 },
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        opacity: 0,
                        animation: "hero-fade 1s ease 0.2s forwards",
                    }}
                >
                    <Box
                        sx={{
                            width: { xs: "32px", md: "56px" },
                            height: "1px",
                            background: "rgba(255, 255, 255, 0.7)",
                            transformOrigin: "left",
                            animation: "hero-line-grow 1s ease 0.3s both",
                        }}
                    />
                    <Typography
                        sx={{
                            fontFamily: '"Inter", sans-serif',
                            fontSize: { xs: "0.62rem", md: "0.7rem" },
                            fontWeight: 500,
                            letterSpacing: "4px",
                            textTransform: "uppercase",
                            color: "rgba(255, 255, 255, 0.75)",
                        }}
                    >
                        {t("hero.badge")}
                    </Typography>
                </Box>
            </Container>

            {/* ═══════════════════════════════════════════
                Main content — centered
            ═══════════════════════════════════════════ */}
            <Container
                maxWidth="md"
                sx={{
                    position: "relative",
                    zIndex: 2,
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    textAlign: "center",
                    px: { xs: 3, md: 6 },
                    py: { xs: 4, md: 6 },
                }}
            >
                {/* Brand signature in italic serif */}
                <Typography
                    sx={{
                        fontFamily: '"Cormorant Garamond", Georgia, serif',
                        fontStyle: "italic",
                        fontWeight: 300,
                        color: "rgba(255, 255, 255, 0.95)",
                        fontSize: { xs: "0.95rem", md: "1.1rem" },
                        letterSpacing: "1px",
                        mb: { xs: 3, md: 4 },
                        opacity: 0,
                        animation: "hero-rise 1s cubic-bezier(0.23,1,0.32,1) 0.4s forwards",
                    }}
                >
                    — {t("hero.signature")}
                </Typography>

                {/* Main heading — serif display */}
                <Typography
                    variant="h1"
                    sx={{
                        color: "#ffffff",
                        mb: { xs: 3, md: 4 },
                        fontWeight: 400,
                        fontSize: { xs: "2.4rem", sm: "3.4rem", md: "4.6rem", lg: "5.2rem" },
                        lineHeight: { xs: 1.15, md: 1.05 },
                        letterSpacing: "-0.5px",
                        maxWidth: "950px",
                        opacity: 0,
                        animation: "hero-rise 1.1s cubic-bezier(0.23,1,0.32,1) 0.55s forwards",
                    }}
                >
                    {t("hero.mainHeading")}
                </Typography>

                {/* Decorative center line */}
                <Box
                    sx={{
                        width: { xs: "44px", md: "60px" },
                        height: "1px",
                        background: "rgba(255, 255, 255, 0.55)",
                        mx: "auto",
                        mb: { xs: 3, md: 4 },
                        transformOrigin: "center",
                        opacity: 0,
                        animation: "hero-line-grow 0.9s cubic-bezier(0.23,1,0.32,1) 0.85s forwards",
                    }}
                />

                {/* Description */}
                <Typography
                    sx={{
                        fontFamily: '"Inter", sans-serif',
                        fontSize: { xs: "0.95rem", md: "1.05rem" },
                        color: "rgba(255, 255, 255, 0.78)",
                        lineHeight: 1.85,
                        fontWeight: 300,
                        maxWidth: "620px",
                        mb: { xs: 4, md: 5 },
                        px: { xs: 0.5, md: 0 },
                        opacity: 0,
                        animation: "hero-rise 1s cubic-bezier(0.23,1,0.32,1) 0.95s forwards",
                    }}
                >
                    {t("hero.description")}
                </Typography>

                {/* Buttons */}
                <Box
                    sx={{
                        display: "flex",
                        gap: { xs: 1.5, md: 2.5 },
                        flexWrap: "wrap",
                        justifyContent: "center",
                        opacity: 0,
                        animation: "hero-rise 1s cubic-bezier(0.23,1,0.32,1) 1.1s forwards",
                    }}
                >
                    <Button
                        onClick={() => onBookingClick()}
                        sx={{
                            fontFamily: '"Inter", sans-serif',
                            fontWeight: 500,
                            fontSize: { xs: "0.7rem", md: "0.75rem" },
                            letterSpacing: "2.5px",
                            textTransform: "uppercase",
                            py: { xs: "0.85rem", md: "1.05rem" },
                            px: { xs: "1.6rem", md: "2.4rem" },
                            borderRadius: 0,
                            background: "#ffffff",
                            color: "#000000",
                            border: "1px solid #ffffff",
                            transition: "all 0.4s cubic-bezier(0.23,1,0.32,1)",
                            "&:hover": {
                                background: "#e0e0e0",
                                borderColor: "#e0e0e0",
                                transform: "translateY(-2px)",
                                boxShadow: "0 14px 40px rgba(255, 255, 255, 0.25)",
                            },
                        }}
                    >
                        {t("hero.buttons.bookAppointment")}
                    </Button>
                    <Button
                        onClick={() =>
                            document.getElementById("our-story")?.scrollIntoView({ behavior: "smooth" })
                        }
                        sx={{
                            fontFamily: '"Inter", sans-serif',
                            fontWeight: 400,
                            fontSize: { xs: "0.7rem", md: "0.75rem" },
                            letterSpacing: "2.5px",
                            textTransform: "uppercase",
                            py: { xs: "0.85rem", md: "1.05rem" },
                            px: { xs: "1.6rem", md: "2.4rem" },
                            borderRadius: 0,
                            background: "transparent",
                            color: "#ffffff",
                            border: "1px solid rgba(255, 255, 255, 0.35)",
                            transition: "all 0.4s cubic-bezier(0.23,1,0.32,1)",
                            "&:hover": {
                                borderColor: "rgba(255, 255, 255, 0.85)",
                                background: "rgba(255, 255, 255, 0.06)",
                                transform: "translateY(-2px)",
                            },
                        }}
                    >
                        {t("hero.buttons.learnMore")}
                    </Button>
                </Box>

                {/* Stats — inline minimal row */}
                <Box
                    sx={{
                        display: "flex",
                        gap: { xs: 3, sm: 5, md: 7 },
                        mt: { xs: 6, md: 8 },
                        flexWrap: "wrap",
                        justifyContent: "center",
                        opacity: 0,
                        animation: "hero-fade 1.2s ease 1.4s forwards",
                    }}
                >
                    {[
                        { number: "20+", label: t("hero.stats.yearsExperience") },
                        { number: "1:1", label: t("hero.stats.expertTeam") },
                        { number: "10k+", label: t("hero.stats.satisfiedClients") },
                    ].map((stat, i) => (
                        <Box
                            key={i}
                            sx={{
                                textAlign: "center",
                                position: "relative",
                                "&:not(:last-of-type)::after": {
                                    content: '""',
                                    position: "absolute",
                                    right: { xs: "-12px", sm: "-20px", md: "-28px" },
                                    top: "50%",
                                    transform: "translateY(-50%)",
                                    width: "1px",
                                    height: "32px",
                                    background: "rgba(255, 255, 255, 0.3)",
                                },
                            }}
                        >
                            <Typography
                                sx={{
                                    fontFamily: '"Cormorant Garamond", serif',
                                    fontSize: { xs: "1.6rem", md: "2rem" },
                                    fontWeight: 400,
                                    color: "#ffffff",
                                    lineHeight: 1,
                                    mb: 0.5,
                                }}
                            >
                                {stat.number}
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: { xs: "0.6rem", md: "0.65rem" },
                                    fontWeight: 500,
                                    letterSpacing: "2.5px",
                                    textTransform: "uppercase",
                                    color: "rgba(255, 255, 255, 0.55)",
                                }}
                            >
                                {stat.label}
                            </Typography>
                        </Box>
                    ))}
                </Box>
            </Container>

            {/* ── Scroll indicator ── */}
            <Box
                sx={{
                    position: "relative",
                    zIndex: 2,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    pb: { xs: "1.5rem", md: "2rem" },
                    gap: 1,
                }}
            >
                <Typography
                    sx={{
                        fontSize: "0.6rem",
                        letterSpacing: "3px",
                        textTransform: "uppercase",
                        color: "rgba(255, 255, 255, 0.45)",
                        fontFamily: '"Inter", sans-serif',
                    }}
                >
                    Scroll
                </Typography>
                <ArrowDownwardIcon
                    sx={{
                        color: "rgba(255, 255, 255, 0.55)",
                        fontSize: "1.1rem",
                        cursor: "pointer",
                        animation: "hero-bounce 2.4s ease-in-out infinite",
                        transition: "color 0.3s ease",
                        "&:hover": { color: "#ffffff" },
                    }}
                    onClick={() =>
                        document.getElementById("about")?.scrollIntoView({ behavior: "smooth" })
                    }
                />
            </Box>
        </Box>
    );
};

export default Hero;
